import React from 'react';
import { shallowEqual } from 'react-redux';
import { Box } from '@apia/theme';
import { useAppSelector } from '../../../../../hooks/hooks';
import store from '../../../../../store';
import { responsiveTableActions } from '../../../../../store/responsiveTableSlice';
import { TResponsiveTableIndexColumns } from '../../../../../store/responsiveTableSlice/types';
import { useResponsiveTableContext } from '../ResponsiveTableContext';
import { TResponsiveTableColumn } from '../types';
import AccordionElement from './AccordionElement';
import NoRegistersRenderer from './NoRegistersRenderer';

const isDebugging = false;
const debuggingRows = 50;

const AccordionRenderer = () => {
  const { name, variants } = useResponsiveTableContext();

  const { columns, rows } = useAppSelector((global) => {
    const tableState = global.responsiveTableSlice[name];
    return {
      columns: tableState?.columns,
      rows: tableState?.rows,
    };
  }, shallowEqual);

  React.useEffect(() => {
    if (isDebugging) {
      console.log('Calculate the index column');
      console.warn(
        `The debug mode is active in AccordionRenderer so the ammount of records is reduced to ${debuggingRows}`,
      );
    }

    /**
     * Método para seleccionar qué columnas se utilizarán
     * como cabezal en cada fila.
     *
     * - Si existe al menos una columna marcada con showInAccordionTitle,
     * todas las columnas que estén marcadas con esta propiedad serán
     * mostradas como parte del título.
     *
     * - Si no existe ninguna, se busca cuál es la primera columna
     * que no tiene ninguna entrada vacía y se utiliza como título
     * del acordión.
     *
     * - Si ninguna columna no tiene ninguna fila vacía, entonces se
     * utiliza la primera columna.
     */

    if (columns && rows) {
      /**
       * Buscar columnas marcadas
       */
      const markedAsTitleColumns: TResponsiveTableIndexColumns = [];
      columns.forEach((column, index) => {
        if (column.showInAccordionTitle)
          markedAsTitleColumns.push({ column, index });
      });
      if (markedAsTitleColumns.length > 0) {
        store.dispatch(
          responsiveTableActions.update({
            tableName: name,
            accordionIndexColumns: markedAsTitleColumns,
          }),
        );
      } else {
        /**
         * No se encontró ninguna columna marcada, se busca la primera sin
         * entradas vacías.
         */
        const columnsLength = columns.length;
        let indexColumn: TResponsiveTableColumn | null = null;
        let currentIndex = 0;

        while (
          (!indexColumn && !isDebugging && currentIndex < columns.length) ||
          (isDebugging && currentIndex < debuggingRows)
        ) {
          let isAnyEmptyRow = false;

          for (const row of rows) {
            if (!row.cells[currentIndex].children) {
              isAnyEmptyRow = true;
              break;
            }
          }
          if (!isAnyEmptyRow && columns[currentIndex]) {
            indexColumn = columns[currentIndex];
          } else {
            currentIndex += 1;
            if (currentIndex === columnsLength) break;
          }
        }
        if (!indexColumn) {
          /**
           * No hay ninguna sin ninguna entrada vacía,
           * se usa la primera columna.
           */
          [indexColumn] = columns;
          currentIndex = 0;
        }
        store.dispatch(
          responsiveTableActions.update({
            tableName: name,
            accordionIndexColumns: [
              { column: indexColumn, index: currentIndex },
            ],
          }),
        );
      }
    }
  }, [columns, name, rows]);

  const [ref, setRef] = React.useState<HTMLDivElement | null>(null);
  React.useEffect(() => {
    const observer = new MutationObserver(([mutation]) => {
      if (mutation.addedNodes) {
        mutation.addedNodes.forEach((current) => {
          if (
            (current as HTMLElement).classList?.contains(
              'responsiveTable__accordionElement',
            )
          )
            (current as HTMLElement).scrollIntoView?.();
        });
      }
    });
    if (ref) observer.observe(ref, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
    };
  }, [ref]);

  return (
    <Box
      className="responsiveTable__accordion"
      {...window.getVariant(
        variants?.accordion ?? 'layout.common.tables.accordion',
      )}
      ref={setRef}
    >
      {!rows || (rows.length === 0 && <NoRegistersRenderer />)}
      {rows &&
        rows
          .slice(0, isDebugging ? debuggingRows : undefined)
          .map((current, rowIndex) => (
            <AccordionElement
              row={current}
              rowIndex={rowIndex}
              key={current.id}
            />
          ))}
    </Box>
  );
};

export default React.memo(AccordionRenderer);
